import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { OUTBOUND_CALL_OUTCOME_LABELS } from '@vagusx/shared';
import type { CampaignResults } from '@vagusx/clients';
import { DateRangePicker } from '@/components/custom/date-range-picker';
import { useCampaignResults } from '@/hooks/use-outreach-results';
import { DATE_RANGE_PRESETS, resolveDateRange, type DateRangeValue } from '@/lib/date-range';
import { REASON_LABELS } from './labels';

/** Every period but "All time": the results API reads at most 366 days. */
const PRESETS = DATE_RANGE_PRESETS.filter((p) => p !== 'all_time');

/**
 * A campaign's Results (ADR-318, H): counts only, for the runs that started in
 * the range. Success is read from the practice's records and counts only what
 * the campaign caused; a patient who booked on their own shows under how runs
 * ended, not as a success.
 */
export function ResultsTab({
  campaignId,
  active,
}: {
  campaignId: string;
  /** The tab is showing; nothing is fetched otherwise. */
  active: boolean;
}): React.ReactElement {
  // Null until the author picks a range: the server's default is the
  // practice's last 30 days in the practice's own timezone, the same figure
  // the list shows, and the answer says which days those were.
  const [value, setValue] = useState<DateRangeValue | null>(null);
  const resolved = value ? resolveDateRange(value) : null;
  const tooLong =
    resolved !== null &&
    differenceInCalendarDays(parseISO(resolved.to), parseISO(resolved.from)) > 365;
  const { data, isLoading, isError } = useCampaignResults(campaignId, resolved, {
    // A picked range that is invalid or too long asks for nothing.
    enabled: active && (value === null || (resolved !== null && !tooLong)),
  });
  const shown: DateRangeValue = value ?? {
    preset: 'custom',
    from: data?.range.from,
    to: data?.range.to,
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Patients who joined in</p>
        <DateRangePicker
          id="campaign-results-range"
          value={shown}
          onChange={setValue}
          presets={PRESETS}
        />
      </div>

      {tooLong && <p className="text-sm text-destructive">Choose a range of a year or less.</p>}
      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Working it out…
        </div>
      )}
      {isError && <p className="text-sm text-destructive">Could not load these results.</p>}
      {data && <ResultsView results={data} />}
    </div>
  );
}

const pct = (n: number, of: number): string => (of > 0 ? `${Math.round((n / of) * 100)}%` : '—');

export function ResultsView({ results }: { results: CampaignResults }): React.ReactElement {
  const { funnel, volume } = results;
  if (funnel.qualified === 0) {
    return (
      <p className="rounded-lg border p-6 text-center text-sm text-muted-foreground">
        Nobody joined this campaign in this range.
      </p>
    );
  }
  const outcomeLabel = (value: string): string =>
    OUTBOUND_CALL_OUTCOME_LABELS.find((o) => o.value === value)?.label ?? value;

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Panel title="From joining to success">
        <table className="w-full text-sm">
          <tbody>
            {(
              [
                ['Joined', funnel.qualified],
                // The server's "contacted": a call placed, or reached another way (a return call).
                ['Contacted', funnel.contacted],
                ['Reached', funnel.reached],
                ['Succeeded', funnel.succeeded],
              ] as const
            ).map(([label, n]) => (
              <tr key={label} className="border-b last:border-0" data-stage={label}>
                <td className="py-2">{label}</td>
                <td className="py-2 text-right tabular-nums">{n.toLocaleString()}</td>
                <td className="w-16 py-2 text-right tabular-nums text-muted-foreground">
                  {pct(n, funnel.qualified)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-2 text-xs text-muted-foreground">
          Succeeded counts only what the campaign caused, read from your records — never from what
          was said on a call.
        </p>
      </Panel>

      <Panel title="Needs a person">
        <CountList
          rows={[
            ['Wrong number', results.needsPerson.wrongNumber],
            ['Asked for a person', results.needsPerson.escalated],
            ['Every try used, never reached', results.needsPerson.exhausted],
          ]}
        />
      </Panel>

      <Panel title="How runs ended">
        <CountList
          rows={results.endedBy.map((e) => [REASON_LABELS[e.reason], e.count] as const)}
          empty="Nobody has finished yet."
        />
      </Panel>

      <Panel title="How calls ended">
        <CountList
          rows={results.dispositions.map((d) => [outcomeLabel(d.disposition), d.count] as const)}
          empty="No call has finished yet."
        />
      </Panel>

      <Panel title="Calls">
        <CountList
          rows={[
            ['Calls placed', volume.calls],
            ['Minutes on calls', volume.callMinutes],
            ['Minutes talking with patients', volume.talkMinutes],
          ]}
        />
      </Panel>

      <Panel title="By version">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th className="pb-1 font-normal">Version</th>
              <th className="pb-1 text-right font-normal">Joined</th>
              <th className="pb-1 text-right font-normal">Succeeded</th>
            </tr>
          </thead>
          <tbody>
            {results.byVersion.map((v) => (
              <tr key={v.versionId} className="border-t">
                <td className="py-2">v{v.version}</td>
                <td className="py-2 text-right tabular-nums">{v.runs.toLocaleString()}</td>
                <td className="py-2 text-right tabular-nums">
                  {v.succeeded.toLocaleString()}{' '}
                  <span className="text-muted-foreground">{pct(v.succeeded, v.runs)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}

function Panel({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <section className="rounded-lg border p-4">
      <h3 className="mb-2 text-sm font-medium">{title}</h3>
      {children}
    </section>
  );
}

function CountList({
  rows,
  empty,
}: {
  rows: ReadonlyArray<readonly [string, number]>;
  empty?: string;
}): React.ReactElement {
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">{empty}</p>;
  return (
    <ul className="divide-y text-sm">
      {rows.map(([label, n]) => (
        <li key={label} className="flex justify-between gap-3 py-2">
          <span>{label}</span>
          <span className="tabular-nums">{n.toLocaleString()}</span>
        </li>
      ))}
    </ul>
  );
}
